import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.closeDetails = this.closeDetails.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
    this.closeMobileFilters = this.closeMobileFilters.bind(this)
    this.isMobile = false
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500).bind(this)
  }

  connectedCallback() {
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    this.bindEventListeners()
    this.bindActiveFacetButtonEvents()
  }

  bindEventListeners() {
    const submitInputs = this.querySelectorAll('.js-filter-submit')
    const filterInputs = this.querySelectorAll(
      '.filter__item input[type="checkbox"]'
    )
    const sortInput = this.querySelector('.js-sort-input')
    const productTypeInput = this.querySelector('.js-type-input')
    const closeButton = this.querySelector('.js-close')

    window.addEventListener('popstate', this.onHistoryChange)
    document.body.addEventListener('click', this.closeDetails)
    submitInputs.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })

    if (!this.isMobile) {
      filterInputs.forEach((element) => {
        element.addEventListener('change', this.debouncedOnSubmit)
      })
      sortInput && sortInput.addEventListener('change', this.debouncedOnSubmit)
      productTypeInput &&
        productTypeInput.addEventListener('change', this.debouncedOnSubmit)
    }

    closeButton?.addEventListener('click', this.closeMobileFilters)
  }

  closeMobileFilters() {
    this.dispatchEvent(
      new CustomEvent('closeModal', {
        bubbles: true,
      })
    )
  }

  closeDetails(event) {
    const detailsElements = this.querySelectorAll('.js-filter')
    detailsElements.forEach((detailsElement) => {
      if (!detailsElement.contains(event.target)) {
        detailsElement.open = false
      }
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest('form'));

    // 🔹 Tracking (GTM-friendly: dataLayer only)
    if (window.__pageType === 'search' || window.__pageType === 'cdp') {
      const allow = /^(filter\.|options\.|pf\.|q$|type$|vendor$|product_type$|availability|filter\.v\.price|filter\.p\.|filter\.t\.)/i;
      const skip  = /^(sections\[\]|page$|section_id$)/i;
      const seen = new Set();

      for (const [name, value] of formData.entries()) {

      if (!value) continue;

      const sel = `[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`;
      const el  = this.querySelector(sel);       // el là <input ...>

      const groupName = el?.dataset.groupName || name; // lấy data-group-name
      // dùng groupName để tracking
        if (!value || skip.test(name) || !allow.test(name)) continue;
        const key = name + '|' + value;
        if (seen.has(key)) continue;
        seen.add(key);

        sendFilterEventDL({
          filter_category: groupName,
          filter_value: String(value),
          page_type: window.__pageType   // 'cdp' or 'search'
        });
        console.log('DL filter event:', {
          filter_category: groupName,
          filter_value: String(value),
          page_type: window.__pageType
        });
      }
    }
    
    const searchParams = new URLSearchParams(formData).toString()
    this.renderPage(searchParams, event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('.js-product-grid')
      .classList.add('loading')

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CollectionProductGrid').innerHTML

    document.getElementById('CollectionProductGrid').innerHTML = innerHTML
  }

  renderFilters(...args) {
    if (this.isMobile) {
      this.renderSingleFormFilters('#CollectionFiltersFormMobile', ...args)
    } else {
      this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
    }
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const filterForm = this.querySelector('#CollectionFiltersForm')
    const newFilterForm = parsedHTML.querySelector('#CollectionFiltersForm')
    const filterFormMobile = this.querySelector('#CollectionFiltersFormMobile')
    const newFilterFormMobile = parsedHTML.querySelector(
      '#CollectionFiltersFormMobile'
    )

    if (this.isMobile) {
      filterFormMobile.innerHTML = newFilterFormMobile.innerHTML
    } else {
      filterForm.innerHTML = newFilterForm.innerHTML
    }

    this.bindEventListeners()
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.js-facets--mobile',
      '.js-facets--desktop',
    ]

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector)
      if (!activeFacetsElement) return
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML
    })

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)

function sendFilterEventDL(payload){
  if (typeof gtag === 'function') {
    gtag('event', 'filter_click', payload);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'filter_click', ...payload });
  }
  // Dev: thêm ?debug=1 vào URL để xem log
  if (location.search.includes('debug=1')) console.log('[filter_click]', payload);
}